import React, { useState, useEffect } from 'react';
import LoadingSpinner from '../components/Lifestyle/LoadingSpinner';
import ErrorDisplay from '../components/Lifestyle/ErrorDisplay';
import LifestyleCard from '../components/Lifestyle/LifestyleCard';
import LifestyleStats from '../components/Lifestyle/LifeStyleStats';
import LifestyleForm from '../components/Lifestyle/LifeStyleForm';
import axios from 'axios';
import { PageHeader, ConfirmDialog } from '../components/admin';

function LifestyleAdminPage() {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingModel, setEditingModel] = useState(null);
  const [modelToDelete, setModelToDelete] = useState(null);
  const [viewMode, setViewMode] = useState('grid');

  useEffect(() => {
    fetchModels();
  }, []);

  const fetchModels = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/lifestyles`);
      setModels(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch models. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!modelToDelete) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/lifestyles/${modelToDelete.id}`);
      setModels((currentModels) => currentModels.filter((model) => model.id !== modelToDelete.id));
      setModelToDelete(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete model.');
    }
  };

  const handleCreate = async (modelData) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/lifestyles`, modelData);
      setModels([...models, response.data]);
      setShowForm(false);
    } catch (err) {
      setError(err.response?.data?.message);
    }
  };

  const handleUpdate = async (modelData) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/lifestyles/${modelData.id}`,
        modelData
      );
      setModels(models.map((model) => (model.id === modelData.id ? response.data : model)));
      setEditingModel(null);
    } catch (err) {
      setError(err.response?.data?.message);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error && models.length === 0) return <ErrorDisplay error={error} onRetry={fetchModels} />;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Lifestyle"
        description="Manage your lifestyle models"
        actions={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
            >
              {viewMode === 'grid' ? 'List view' : 'Grid view'}
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => {
                setShowForm(!showForm);
                setEditingModel(null);
              }}
            >
              {showForm ? 'View all' : 'Add new'}
            </button>
          </>
        }
      />

      {error && (
        <div className="panel px-4 py-3 text-sm text-accent-soft border-accent-dim">{error}</div>
      )}

      {modelToDelete && (
        <ConfirmDialog
          title="Delete model"
          message={`Delete “${modelToDelete.name || 'this model'}”? This action cannot be undone.`}
          confirmLabel="Delete model"
          onConfirm={handleDelete}
          onCancel={() => setModelToDelete(null)}
        />
      )}

      <LifestyleStats count={models.length} onRefresh={fetchModels} />

      {showForm ? (
        <div className="max-w-2xl">
          <LifestyleForm onSubmit={handleCreate} onCancel={() => setShowForm(false)} />
        </div>
      ) : editingModel ? (
        <div className="max-w-2xl">
          <LifestyleForm initialData={editingModel} onSubmit={handleUpdate} onCancel={() => setEditingModel(null)} />
        </div>
      ) : (
        <>
          {models.length === 0 ? (
            <div className="card py-16 flex flex-col items-center text-center">
              <h3 className="text-lg font-semibold text-white">No models yet</h3>
              <p className="text-sm text-gray-500 mt-1">Create your first lifestyle model to get started</p>
              <button type="button" className="btn-primary mt-5" onClick={() => setShowForm(true)}>
                Create first model
              </button>
            </div>
          ) : (
            <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4' : 'space-y-4'}>
              {models.map((model) => (
                <div
                  key={model.id}
                  className={viewMode === 'list' ? 'flex items-center gap-6' : ''}
                >
                  <LifestyleCard
                    lifestyle={model}
                    isAdmin={true}
                    onEdit={setEditingModel}
                    onDelete={(model) => setModelToDelete(model)}
                  />
                  {viewMode === 'list' && (
                    <div className="hidden md:flex flex-col gap-2 ml-auto shrink-0">
                      <button type="button" className="btn-secondary !py-1.5 !px-3" onClick={() => setEditingModel(model)}>
                        Quick edit
                      </button>
                      <button type="button" className="btn-danger !py-1.5 !px-3" onClick={() => setModelToDelete(model)}>
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default LifestyleAdminPage;
